import { CONTACT_SUBJECTS, CONTACT_SUBJECT_KEYS, CONTACT_MESSAGE_MAX, isValidEmail, type ContactBody, type ContactSubject } from '@akrababul/shared';
import { el } from './dom.js';
import { apiPost, ApiError } from './api.js';
import type { SessionInfo } from './nav.js';

export interface CaptchaApi {
  render: (el: HTMLElement, opts: { sitekey: string; theme?: string }) => string;
  getResponse: (id: string) => string | undefined;
  reset: (id: string) => void;
}
const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY ?? '';
let turnstile: Promise<CaptchaApi> | null = null;

/**
 * Turnstile betiği her sayfada değil, kullanıcı iletişim penceresini açınca yüklenir:
 * footer tüm sayfalarda olduğu için üçüncü taraf betiğini peşinen yüklemek istemiyoruz.
 */
export function loadTurnstile(): Promise<CaptchaApi> {
  turnstile ??= new Promise<CaptchaApi>((resolve, reject) => {
    const w = window as unknown as Record<string, unknown>;
    const cb = '__akTurnstileReady';
    w[cb] = () => resolve(w['turnstile'] as CaptchaApi);
    const s = document.createElement('script');
    s.src = `https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=${cb}`;
    s.async = true; s.defer = true;
    s.addEventListener('error', () => reject(new Error('Turnstile yüklenemedi')));
    document.head.append(s);
  });
  return turnstile;
}

export interface ContactFormOptions {
  /** Oturum varsa ad (doluysa) ve e-posta sorulmaz; alanlar gizli kalır ama gövdeye yazılır. */
  session: SessionInfo | null;
  loadCaptcha?: () => Promise<CaptchaApi>;
}

/** İletişim formu: giriş gerektirmez, robot doğrulaması zorunludur. Başarıda form kalkar, yalnızca mesaj kalır. */
export function mountContactForm(host: HTMLElement, opts: ContactFormOptions): void {
  const s = opts.session;
  const name = el('input', { type: 'text', id: 'cName', autocomplete: 'name', maxlength: '100', required: '' });
  const email = el('input', { type: 'email', id: 'cEmail', autocomplete: 'email', maxlength: '200', required: '' });
  const subject = el('select', { id: 'cSubject' }, CONTACT_SUBJECT_KEYS.map((k) => el('option', { value: k }, [CONTACT_SUBJECTS[k]])));
  const message = el('textarea', { id: 'cMessage', rows: '5', maxlength: String(CONTACT_MESSAGE_MAX), required: '' });
  const captcha = el('div', { class: 'captcha' });
  const send = el('button', { type: 'submit' }, ['Gönder']);
  const ok = el('div', { class: 'notice hidden' });
  const err = el('div', { class: 'error hidden' });
  const show = (box: HTMLElement, t: string) => { box.textContent = t; box.classList.remove('hidden'); };

  const nameField = el('div', { class: 'field' }, [el('label', { for: 'cName' }, ['Ad Soyad']), name]);
  const emailField = el('div', { class: 'field' }, [el('label', { for: 'cEmail' }, ['E-posta']), email]);
  if (s) {
    name.value = s.name.trim();
    email.value = s.email;
    nameField.classList.toggle('hidden', name.value.length > 0);
    emailField.classList.add('hidden');
  }

  const form = el('form', { class: 'contact-form', novalidate: '' }, [
    el('div', { class: 'contact-grid' }, [nameField, emailField]),
    el('label', { for: 'cSubject' }, ['Konu']), subject,
    el('label', { for: 'cMessage' }, [`Mesajınız (düz metin, en fazla ${CONTACT_MESSAGE_MAX} karakter)`]), message,
    captcha, err,
    el('p', {}, [send]),
  ]);
  host.replaceChildren(ok, form);

  let widgetId: string | null = null;
  const api = (opts.loadCaptcha ?? loadTurnstile)().then((t) => { widgetId = t.render(captcha, { sitekey: SITE_KEY }); return t; })
    .catch(() => { captcha.replaceChildren(el('p', { class: 'muted' }, ['Robot doğrulaması yüklenemedi. Reklam engelleyicinizi kapatıp sayfayı yenileyin.'])); return null; });

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    err.classList.add('hidden');
    const body: ContactBody = {
      name: name.value.trim(), email: email.value.trim(),
      subject: subject.value as ContactSubject, message: message.value.trim(),
    };
    if (!body.name) return show(err, 'Adınızı yazın.');
    if (!isValidEmail(body.email)) return show(err, 'Geçerli bir e-posta adresi yazın.');
    if (!body.message) return show(err, 'Mesajınızı yazın.');
    const t = await api;
    const token = t && widgetId !== null ? t.getResponse(widgetId) ?? '' : '';
    if (!token) return show(err, 'Robot doğrulamasını tamamlayın.');
    send.disabled = true;
    try {
      await apiPost('/api/contact', body, { 'x-captcha-response': token });
      // Boşaltılmış bir form değil, yalnızca sonuç görünsün.
      form.remove();
      show(ok, 'Mesajınız iletildi. En kısa sürede dönüş yapacağız.');
      return;
    } catch (e2) {
      show(err, e2 instanceof ApiError ? e2.message : 'Mesaj gönderilemedi. Biraz sonra tekrar deneyin.');
    }
    // Token tek kullanımlıktır; hatada yenilenir.
    if (t && widgetId !== null) t.reset(widgetId);
    send.disabled = false;
  });
}
